import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { useNavigate } from 'react-router-dom';
import { storePendingAssessment } from '@/utils/assessmentStorage';
import { Place } from '@/types';

interface SaveBenchmarkData {
  selectedPlace: Place;
  peerPlaces: Place[];
  activeTab: string;
  metricsData?: Record<string, unknown>;
}

export const useSaveBenchmarkComparison = (data: SaveBenchmarkData) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    console.log('Starting save benchmark comparison...', data);
    setIsSaving(true);
    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        console.log('Auth error or no user:', authError);
        toast({
          title: "Authentication required",
          description: "Please sign in to save your comparison",
          variant: "destructive",
        });
        storePendingAssessment({
          benchmarkData: {
            selectedPlace: data.selectedPlace,
            peerPlaces: data.peerPlaces,
            activeTab: data.activeTab,
          },
          returnTo: '/benchmarking/new'
        });
        navigate('/login');
        return;
      }

      console.log('Authenticated user:', user.id);

      // Get the most recent comparison for this user
      const { data: existingComparisons, error: checkError } = await supabase
        .from('benchmark_comparisons')
        .select('id, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1);

      if (checkError) {
        console.error('Error checking existing comparison:', checkError);
        throw checkError;
      }

      console.log('Existing comparisons check:', existingComparisons);

      let result;
      
      if (existingComparisons && existingComparisons.length > 0) {
        const mostRecentComparison = existingComparisons[0];
        console.log('Updating most recent comparison:', mostRecentComparison.id);
        // Update existing comparison
        result = await supabase
          .from('benchmark_comparisons')
          .update({
            selected_place: data.selectedPlace,
            peer_places: data.peerPlaces,
            active_tab: data.activeTab,
            metrics_data: data.metricsData,
            updated_at: new Date().toISOString(),
          })
          .eq('id', mostRecentComparison.id)
          .select();
      } else {
        console.log('Creating new comparison for user:', user.id);
        // Insert new comparison
        result = await supabase
          .from('benchmark_comparisons')
          .insert({
            user_id: user.id,
            selected_place: data.selectedPlace,
            peer_places: data.peerPlaces,
            active_tab: data.activeTab,
            metrics_data: data.metricsData,
          })
          .select();
      }

      const { error } = result;

      if (error) {
        console.error('Error saving comparison:', error);
        throw error;
      }

      console.log('Comparison saved successfully');
      return result;
    } catch (error) {
      console.error('Error in handleSave:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  return { handleSave, isSaving };
}; 